/**
 * Blog content for podus.ai — the Podus AI product blog.
 *
 * Written in Podus AI's own first-party voice ("we", "our platform"). Every factual claim (what the
 * product does, who it serves, usage numbers) reflects real, publicly stated facts about Podus AI
 * and Citizens' Gavel — keep it accurate; do not invent statistics or quotes.
 *
 * These are real, visible, indexable articles: the target topics (AI for justice, tech for good,
 * Nigeria's AI ecosystem) live in genuine content that can rank and earn links.
 */

export type Block =
    | { type: 'p'; html: string }
    | { type: 'h2'; text: string }
    | { type: 'ul'; items: string[] }
    | { type: 'quote'; text: string; cite: string };

export interface Post {
    slug: string;
    title: string;
    /** ~150-160 char meta description. */
    description: string;
    /** ISO date, used for <time> and Article schema. */
    date: string;
    readingTime: string;
    tags: string[];
    /** One-line teaser for the blog index card. */
    excerpt: string;
    content: Block[];
}

export const POSTS: Post[] = [
    {
        slug: 'ai-for-good-podus-ai-citizens-gavel',
        title: 'AI for Good, in Practice: The Story Behind Podus AI',
        description:
            'Podus AI is what “AI for good” looks like when it ships: a free legal assistant from Citizens’ Gavel, trained on Nigerian law and built for emergencies.',
        date: '2026-09-04',
        readingTime: '4 min read',
        tags: ['AI for Good', 'Tech for Good', 'Access to Justice', 'Legal Technology', 'AI in Nigeria'],
        excerpt:
            'Tech for good is easy to say. We have spent years building the lawyer network, the tools and now the AI assistant behind a single claim: that legal help should not depend on income.',
        content: [
            {
                type: 'p',
                html: 'Every few months a new phrase arrives for technology built to do something other than make money. <strong>Tech for good</strong>. <strong>AI for good</strong>. We use both, and we are wary of both, because the phrase is easy and the working product is not.',
            },
            {
                type: 'p',
                html: 'Podus AI is our answer to the phrase. It is a free AI legal assistant built by <a href="https://podus.org" target="_blank" rel="noopener">Citizens’ Gavel</a>, trained on Nigerian law and reachable over WhatsApp. It exists because of years of unglamorous work that came before it.',
            },
            { type: 'h2', text: 'What “AI for good” means to us' },
            {
                type: 'p',
                html: 'It means starting from who is being left out. In Nigeria, needing a lawyer and being able to afford one are two different things, and the gap falls hardest on people facing exactly the moments the law is supposed to cover: a wrongful arrest, an illegal eviction, a demand for a bribe.',
            },
            {
                type: 'p',
                html: 'So we did not build a general assistant with a social-impact page bolted on. We built one deliberately tuned to move fastest on the cases where delay costs a person their freedom:',
            },
            {
                type: 'ul',
                items: [
                    'Police extortion and brutality',
                    'Illegal arrest and detention',
                    'Fundamental human-rights violations',
                    'Gender-based violence and abuse',
                ],
            },
            {
                type: 'p',
                html: 'And we made it end in something usable. Podus AI drafts the petitions, complaint letters and Freedom of Information requests that go to the bodies which can actually act: the National Human Rights Commission, the Public Complaints Commission, the FCCPC, state mediation centres. Advice is where most legal chatbots stop. A document an institution has to answer is where a case starts.',
            },
            { type: 'h2', text: 'The years before the AI' },
            {
                type: 'p',
                html: 'Citizens’ Gavel was founded in 2017 to make justice more accessible in Nigeria, long before any of this could be automated. We built a network of volunteer lawyers and the tools to support their casework. During the 2020 #EndSARS protests that network grew to 250 lawyers and handled more than 400 intervention cases for people who had been arrested and detained. In 2021 we launched Justice Clock, a case-file tracking system for the courts.',
            },
            {
                type: 'p',
                html: 'Podus AI, launched in 2024, is the AI-native step in that same line of work. It did not replace the lawyers. It sits on top of the network they built, and it is how far more people now reach them.',
            },
            {
                type: 'quote',
                text: 'Podus AI is first aid for legal problems. It is not a substitute for a lawyer, but it is the reason many more people ever get to one.',
                cite: 'The Podus AI team',
            },
            { type: 'h2', text: 'Good is measured in cases, not claims' },
            {
                type: 'p',
                html: 'The honest test of a <strong>tech for good</strong> project is whether the team behind it can tell you how many people it served. More than 33,000 people have used Podus AI since launch, and Citizens’ Gavel has handled over 6,500 legal interventions since 2017. Those are not impressive numbers by consumer-technology standards. We report them anyway, because each one is a person who got further than they would have alone.',
            },
            { type: 'h2', text: 'Where we go next' },
            {
                type: 'p',
                html: 'Podus AI already draws on Nigerian, Ghanaian and UK law, and we are working toward wider coverage across the continent. Around it sits the rest of the Podus ecosystem: contract analysis, background checks, and Podus Multidoor for filing disputes into multi-door courthouses. Together they let a matter travel from a first question to a resolved case without leaving one place.',
            },
            {
                type: 'p',
                html: 'That is the whole ambition, and it is a modest one: not AI that impresses, but AI that gets an ordinary person heard. Try it at <a href="https://chat.podus.ai" target="_blank" rel="noopener">chat.podus.ai</a>.',
            },
        ],
    },
    {
        slug: 'ai-for-justice-nigeria-podus-ai',
        title: 'AI for Justice in Nigeria: How Podus AI Is Widening Access to Justice',
        description:
            'Podus AI is an AI-for-justice platform trained on Nigerian law, built to help everyday Nigerians get legal help in minutes. Here is how it works.',
        date: '2026-07-20',
        readingTime: '5 min read',
        tags: ['AI for Justice', 'AI in Nigeria', 'Access to Justice', 'Legal Technology'],
        excerpt:
            'For millions of Nigerians, a lawyer is out of reach when it matters most. We built Podus AI to put legal guidance one message away, trained on Nigerian law.',
        content: [
            {
                type: 'p',
                html: 'In Nigeria, needing a lawyer and being able to afford one are two very different things. When a market trader is detained without charge, when a tenant is threatened with an illegal eviction, or when a young person is stopped and extorted by the police, the law is on their side, but the help to enforce it usually is not. We built <strong>Podus AI</strong> to close that gap.',
            },
            {
                type: 'p',
                html: 'Podus AI is our “AI for Justice” platform: an AI legal assistant designed to give ordinary people fast, free legal guidance and, when they need it, a route to a real lawyer. It is built by <a href="https://podus.org" target="_blank" rel="noopener">Citizens’ Gavel</a>, the civic-technology organisation behind years of access-to-justice work in Nigeria.',
            },
            { type: 'h2', text: 'What Podus AI actually does' },
            {
                type: 'p',
                html: 'We designed Podus AI to be reached the way you already communicate. It is free, and it works over WhatsApp, the web and our mobile app, covering Nigerian law across every locality. You can ask a legal question in plain language, understand your rights, and crucially draft the documents that move a complaint forward.',
            },
            {
                type: 'p',
                html: 'That last part matters most. Podus AI helps you draft petitions and complaint letters to the bodies that can actually act: the National Human Rights Commission, the Public Complaints Commission (Ombudsman), the FCCPC and state mediation centres. It turns a vague sense of “this is unfair” into a document an institution has to respond to.',
            },
            { type: 'h2', text: 'Trained on Nigerian law and built for emergencies' },
            {
                type: 'p',
                html: 'Podus AI is trained on the overwhelming majority of Nigerian law, so its answers reflect the rules that actually apply where you live. We tuned it deliberately to prioritise urgent, freedom-threatening situations rather than to behave like a general chatbot.',
            },
            { type: 'p', html: 'That means Podus AI is built to move fastest on cases like:' },
            {
                type: 'ul',
                items: [
                    'Police extortion and brutality',
                    'Fundamental human-rights violations',
                    'Illegal arrest and detention',
                    'Gender-based violence and abuse',
                ],
            },
            {
                type: 'quote',
                text: 'The goal of the project, in its simplest form, is to help the most vulnerable in society get access to justice.',
                cite: 'The Podus AI team',
            },
            {
                type: 'p',
                html: 'Podus AI is built to work <em>with</em> lawyers, not to replace them. It handles the first, high-volume layer of legal questions, the ones that do not need a courtroom. That frees human advocates to focus on the cases that do. For many people, that first answer is the difference between giving up and taking action.',
            },
            { type: 'h2', text: 'Years of work behind one assistant' },
            {
                type: 'p',
                html: 'Podus AI did not appear overnight. Citizens’ Gavel was founded in 2017 to make justice more accessible in Nigeria, and we have spent years building the tools and the volunteer-lawyer network that Podus AI now sits on top of. During the 2020 #EndSARS protests, that network handled hundreds of intervention cases for people who had been arrested and detained. We later built Justice Clock, a case-tracking system launched with the Lagos State Government.',
            },
            {
                type: 'p',
                html: 'Podus AI, launched in 2024, is the AI-native step in that journey. More than 33,000 people have already used it, part of the 6,500+ legal interventions Citizens’ Gavel has handled since we started.',
            },
            { type: 'h2', text: 'Why this matters' },
            {
                type: 'p',
                html: 'Access to justice has always been rationed by cost and geography. An AI assistant trained on Nigerian law, reachable over WhatsApp and free to use, changes that maths, not by replacing the courts, but by helping far more people take the first step. That is the promise we are building toward: legal help that meets people where they are.',
            },
            {
                type: 'p',
                html: 'Try it for yourself at <a href="https://chat.podus.ai" target="_blank" rel="noopener">chat.podus.ai</a>.',
            },
        ],
    },
    {
        slug: 'tech-for-good-nigeria-ai-ecosystem',
        title: 'Tech for Good: Where Podus AI Fits in Nigeria’s Emerging AI Ecosystem',
        description:
            'Nigeria’s AI ecosystem is growing fast. Here is how we think about “tech for good,” responsible AI, and building Podus AI for justice across Africa.',
        date: '2026-07-20',
        readingTime: '4 min read',
        tags: ['Tech for Good', 'AI Ecosystem', 'AI in Africa', 'Emerging AI Trends', 'Responsible AI'],
        excerpt:
            'Much of the AI conversation is about productivity and profit. We built Podus AI on a different bet: that the same tools can widen access to justice, health and rights.',
        content: [
            {
                type: 'p',
                html: 'Africa is in the middle of an AI moment. New models, startups and products appear every month, and Nigeria sits near the centre of that <strong>emerging AI ecosystem</strong>. But alongside the familiar story of AI-for-productivity, we are betting on a quieter and, we think, more important idea: <strong>tech for good</strong>, using the same technology to solve civic problems that markets have historically ignored.',
            },
            {
                type: 'p',
                html: 'Podus AI is what that looks like in practice for us.',
            },
            { type: 'h2', text: 'Our “tech for good” thesis' },
            {
                type: 'p',
                html: 'Tech for good starts from a different question. Instead of “what can we sell?”, we ask “who is being left out, and can technology reach them?” In justice, the answer is stark: legal help is expensive and concentrated in cities, while the people who need it most are often poor and far from a courtroom.',
            },
            {
                type: 'p',
                html: 'An AI assistant changes the unit economics of that problem. Once a model is trained on the law, the marginal cost of answering one more person’s question is close to zero, which is exactly why we can offer Podus AI free of charge, and intend to keep it that way.',
            },
            { type: 'h2', text: 'What responsible AI looks like in the justice sector' },
            {
                type: 'p',
                html: 'Justice is a high-stakes domain, so for us “responsible AI” is not a slogan. It is a design constraint. Two choices in how we build Podus AI are worth calling out:',
            },
            {
                type: 'ul',
                items: [
                    'We ground it in actual Nigerian law rather than generic web text, so answers reflect the jurisdiction a user really lives in.',
                    'We position it as a first step that complements lawyers and routes serious matters to human advocates, rather than pretending to replace legal judgement.',
                ],
            },
            {
                type: 'p',
                html: 'That framing, AI as an on-ramp to justice with humans firmly in the loop, is a template we believe other high-stakes sectors like health, welfare and consumer rights can learn from as they adopt AI.',
            },
            { type: 'h2', text: 'A pan-African trajectory' },
            {
                type: 'p',
                html: 'Our ambition does not stop at one country. Podus AI already draws on Nigerian, UK and Ghanaian law, and we are working toward expanding across more of the continent. That reflects a broader reality of the African AI ecosystem: the most durable products are built for local context first, then extended across borders that share legal and social systems.',
            },
            { type: 'h2', text: 'The bigger picture' },
            {
                type: 'p',
                html: 'Emerging AI trends are usually measured in benchmarks and funding rounds. But the trend we care about most in Africa is distributional: whether AI narrows or widens the gap between those who can afford expert help and those who cannot. Podus AI is our bet that it can narrow it, and a reminder that the most valuable thing an AI ecosystem can produce is not just efficiency, but access.',
            },
            {
                type: 'p',
                html: 'See what we are building at <a href="https://chat.podus.ai" target="_blank" rel="noopener">chat.podus.ai</a>.',
            },
        ],
    },
];

export const getPost = (slug: string): Post | undefined => POSTS.find((p) => p.slug === slug);
